import { Button, Spin, message } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";

import styles from "@/components/Account/PersonalInformation/PersonalInformation.module.css";
import PersonalInformationEditForm from "@/components/Account/PersonalInformation/PersonalInformationEditForm";
import { DATE_FORMAT_DISPLAY, IMAGES } from "@/constant";
import { getUserData } from "@/services/Auth/AuthApi";
import { useAuthStore } from "@/stores/authStore";
import { GetCustomerData } from "@/types/authService";

const PersonalInformation: FC = () => {
  const customer = useAuthStore((state) => state?.customer);
  const userId = customer?.id;
  const updateState = useAuthStore((state) => state?.updateState);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [personalInfoData, setPersonalInfoData] = useState<GetCustomerData>();

  const personalInfoFields: { label: string; key: keyof GetCustomerData }[] = [
    { label: "Name", key: "name" },
    { label: "ID Type", key: "id_type" },
    { label: "ID Number", key: "id_number" },
    { label: "Login ID", key: "customer_id" },
    { label: "Nationality", key: "nationality" },
    { label: "Ethnicity", key: "ethnicity" },
    { label: "Country", key: "country" },
    { label: "City", key: "city" },
    { label: "State", key: "state" },
    { label: "DOB", key: "date_of_birth" },
    { label: "Sex", key: "sex" },
    { label: "Occupation", key: "occupation" },
    { label: "Occupation Details", key: "occupation_details" },
    { label: "Phone Number", key: "mobile_number" },
    { label: "Email", key: "email" },
    { label: "Expiration Date", key: "expiry_date" },
    { label: "Communication Medium", key: "communication_medium" },
    { label: "Preferred 2FA Medium For OTP", key: "two_fa_medium" },
    { label: "TIN Number", key: "tin_number" },
    { label: "Postal Code", key: "postal_code" },
  ];

  const getPersonalInfoHelper = async (): Promise<void> => {
    try {
      setLoading(true);
      const { data } = await getUserData();
      setPersonalInfoData(data);
      if (customer) {
        updateState({ customer: { ...customer, name: data.name } });
      }
    } catch (error: unknown) {
      if (axios.isAxiosError<{ message: string }>(error)) {
        message.error(error?.response?.data?.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void getPersonalInfoHelper();
  }, []);

  return (
    <div className={styles.mainContainer}>
      {loading ? (
        <Spin fullscreen />
      ) : (
        <>
          <div className={styles.headingSection}>
            <div className={styles.headingText}>Personal information</div>
            {!isEdit ? (
              <Button
                shape="default"
                icon={<img src={IMAGES.EDIT_ICON_BUTTON} />}
                className={styles.editButton}
                onClick={() => setIsEdit(true)}
              />
            ) : null}
          </div>
          {isEdit && personalInfoData ? (
            <PersonalInformationEditForm
              onClose={(data?: GetCustomerData) => {
                void getPersonalInfoHelper();
                setIsEdit(false);
              }}
              editData={personalInfoData}
            />
          ) : (
            <div className={styles.personalInfoSection}>
              {personalInfoFields.map(({ label, key }) => {
                let value = personalInfoData?.[key] || "-";

                if (key === "communication_medium" || key === "two_fa_medium") {
                  const field = personalInfoData?.[key];

                  if (Array.isArray(field) && field.length) {
                    const first = field[0];
                    value = first.charAt(0).toUpperCase() + first.slice(1);
                  } else {
                    value = "-";
                  }
                }

                if (key === "mobile_number" && personalInfoData?.dial_code) {
                  value = `${personalInfoData.dial_code} ${personalInfoData.mobile_number}`;
                }

                if (key === "expiry_date" && personalInfoData?.expiry_date) {
                  value = dayjs(personalInfoData.expiry_date).format(DATE_FORMAT_DISPLAY);
                }

                if (key === "date_of_birth" && personalInfoData?.date_of_birth) {
                  value = dayjs(personalInfoData.date_of_birth).format(DATE_FORMAT_DISPLAY);
                }

                return (
                  <div key={key} className={styles.singleInfoWrapper}>
                    <div className={styles.infoTitle}>{label}</div>
                    <div className={styles.info} data-dd-privacy="mask">
                      {value}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PersonalInformation;
